import type { QueryClient } from '@tanstack/react-query';

export type SchedulingInvalidateGroup =
  | 'calendar'
  | 'shifts'
  | 'timeOff'
  | 'shiftTypes'
  | 'timeOffTypes'
  | 'weeklyTemplates'
  | 'assignments'
  | 'leave'
  | 'staff'
  | 'busyTime';

/** Query-key prefixes per group (TanStack Query matches by prefix). */
const GROUP_PREFIXES: Record<SchedulingInvalidateGroup, readonly (readonly string[])[]> = {
  calendar: [['scheduling', 'calendar']],
  shifts: [
    ['scheduling', 'shifts'],
    ['scheduling', 'shift'],
  ],
  timeOff: [['scheduling', 'time-off']],
  shiftTypes: [['scheduling', 'shift-types']],
  timeOffTypes: [['scheduling', 'time-off-types']],
  weeklyTemplates: [
    ['scheduling', 'weekly-templates'],
    ['scheduling', 'weekly-template'],
  ],
  assignments: [['scheduling', 'assignments']],
  leave: [
    ['scheduling', 'leave-entitlements'],
    ['scheduling', 'leave-balance'],
    ['scheduling', 'leave-ledger'],
    ['scheduling', 'leave-report'],
  ],
  staff: [['scheduling', 'staff']],
  busyTime: [
    ['scheduling', 'busy-times'],
    ['scheduling', 'calendar'],
    ['scheduling', 'calendar-slots'],
    ['scheduling', 'availability'],
  ],
};

export function invalidateScheduling(
  queryClient: QueryClient,
  groups: readonly SchedulingInvalidateGroup[]
): Promise<void> {
  const seen = new Set<string>();
  const tasks: Promise<void>[] = [];

  for (const group of groups) {
    for (const prefix of GROUP_PREFIXES[group]) {
      const key = prefix.join('\0');
      if (seen.has(key)) continue;
      seen.add(key);
      tasks.push(
        queryClient.invalidateQueries({ queryKey: [...prefix] }).then(() => undefined)
      );
    }
  }

  return Promise.all(tasks).then(() => undefined);
}
